import { OAuthTokenResponse, OAuthTokens } from './authentication.types';

/** Maps the snake_case token endpoint response to the stored credential pair. */
export function toOAuthTokens(response: OAuthTokenResponse): OAuthTokens {
  return { accessToken: response.access_token, refreshToken: response.refresh_token };
}

export const CREDENTIALS_KEY = 'credentials';
/** Second localStorage slot: the operator's own credentials, stashed while impersonating. */
export const ORIGIN_CREDENTIALS_KEY = 'originCredentials';

export function readStoredCredentials(key: string): OAuthTokens | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(atob(raw));
  } catch {
    return null;
  }
}

export function readCredentials(): OAuthTokens | null {
  return readStoredCredentials(CREDENTIALS_KEY);
}

/**
 * Decodes a base64URL (RFC 4648 §5) string — the alphabet JWT segments actually use
 * (`-`/`_`, no padding), NOT plain base64 (`+`/`/`, `=`-padded). `atob()` throws on `-`/`_`, so
 * decoding a real JWT segment with plain `atob()` silently fails (caught, treated as "expired" /
 * "no actor claim") whenever the payload's encoding happens to contain one of those two
 * characters — a data-dependent bug, not a malformed-input one.
 */
function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const paddingNeeded = (4 - (base64.length % 4)) % 4;
  return atob(base64 + '='.repeat(paddingNeeded));
}

export function isTokenExpired(token: string): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * Reads the subject claim (`sub`) off an access token, i.e. the account the token acts AS. Together
 * with `getActorAccountId` it forms the `(account, actor)` identity tuple `TokenStore` compares to
 * decide whether a write changed the session identity. Returns `null` for no token, no `sub`, or an
 * unparsable one.
 */
export function getAccountId(token: string): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1])) as { sub?: unknown };
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * Reads the actor claim (`act.sub`, RFC 8693) off an access token, i.e. the real
 * account id behind an impersonated session. Returns `null` for a normal token (no `act` claim)
 * or an unparsable one. `TokenStore.isImpersonating` derives from this — the token is the
 * authority, never the presence of a stash.
 */
export function getActorAccountId(token: string): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1])) as {
      act?: { sub?: unknown };
    };
    return typeof payload.act?.sub === 'string' ? payload.act.sub : null;
  } catch {
    return null;
  }
}
